import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import DashboardLayout from '../../components/layouts/dashboard/DashboardLayout';
import DataTable, { TagCell, AmountCell } from '../../components/dashboard/tables/DataTable';
import { PrimaryButton, PlusIcon } from '../../components/dashboard/common/Buttons';
import { useOrganization } from '../../contexts/OrganizationContext';

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
`;

// Example data (preserved from original)
const quotes = [
  {
    date: '31 Mar 2025',
    category: 'D&O Insurance',
    name: 'Premium Plan Quote',
    amount: '75k',
    status: 'Pending'
  },
  {
    date: '15 Mar 2025',
    category: 'Cyber Insurance',
    name: 'Basic Coverage Quote',
    amount: '45k',
    status: 'Under Review'
  }
];

const externalPolicies = [
  {
    date: '1 Oct 2024',
    category: 'Property Insurance',
    name: 'Office Space Coverage',
    amount: '100k',
    provider: 'ExternalCo Insurance'
  },
  {
    date: '15 Sep 2024',
    category: 'Health Insurance',
    name: 'Employee Health Plan',
    amount: '75k',
    provider: 'HealthFirst Ltd'
  }
];

// Table configurations
const policyColumns = [
  { key: 'startDate', label: 'Start Date', width: '15%' },
  { key: 'typeOfCover', label: 'Type of Cover', width: '20%' },
  { key: 'policyNumber', label: 'Policy Number', width: '30%' },
  { key: 'annualPremium', label: 'Annual Premium', width: '15%' },
  { key: 'status', label: 'Status', width: '20%' }
];

const externalPolicyColumns = [
  { key: 'date', label: 'Start Date', width: '15%' },
  { key: 'category', label: 'Type of Cover', width: '20%' },
  { key: 'name', label: 'Policy Number', width: '30%' },
  { key: 'amount', label: 'Annual Premium', width: '15%' },
  { key: 'provider', label: 'Provider', width: '20%' }
];

const getStatusTagProps = (status) => ({
  background: status === 'Active' ? '#E8F5E9' : '#FFF3E0',
  color: status === 'Active' ? '#2E7D32' : '#E65100'
});

const renderPolicyCell = (key, value, row) => {
  switch (key) {
    case 'annualPremium':
      return <AmountCell value={value} />;
    case 'typeOfCover':
    case 'status':
      return <TagCell value={value} getTagProps={key === 'status' ? getStatusTagProps : undefined} />;
    case 'startDate':
      return new Date(value).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    default:
      return value;
  }
};

const Policies = () => {
  const navigate = useNavigate();
  const { organizationId } = useOrganization();
  const [activePolicies, setActivePolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolicies = async () => {
      if (!organizationId) return;

      try {
        console.log('Fetching policies for organization:', organizationId);
        const response = await fetch(`/api/dashboard/policies/${organizationId}`);
        const result = await response.json();

        if (result.success) {
          console.log('Fetched policies:', result.data);
          setActivePolicies(result.data);
        } else {
          console.error('Failed to fetch policies:', result.error);
        }
      } catch (error) {
        console.error('Error fetching policies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicies();
  }, [organizationId]);

  const handlePolicyClick = (policy) => {
    navigate('/dashboard/pp-policy', { 
      state: { policy }
    });
  };

  return (
    <DashboardLayout title="Policies" currentPath="/dashboard/policies">
      <HeaderSection>
        <PrimaryButton>
          <PlusIcon />
          Create
        </PrimaryButton>
      </HeaderSection>
      
      <DataTable 
        title="Quotes"
        columns={policyColumns}
        data={quotes}
        renderCell={renderPolicyCell}
        emptyState="No quotes available"
      />
      
      <DataTable 
        title="Active Policies"
        columns={policyColumns}
        data={activePolicies}
        renderCell={renderPolicyCell}
        onRowClick={handlePolicyClick}
        emptyState={loading ? "Loading policies..." : "No active policies"}
      />
      
      <DataTable 
        title="External Policies"
        columns={externalPolicyColumns}
        data={externalPolicies}
        renderCell={renderPolicyCell}
        emptyState="No external policies"
      />
    </DashboardLayout>
  );
};

export default Policies; 